import getActiveComp from 'common/src/modules/creativeTemplates/copilot/agents/adBuilderAgent/utils/getActiveComp';
import duplicateLayers from 'common/src/modules/creativeTemplates/store/creativeTemplatePage/formatMovies/actions/duplicateLayers';
import {ToolExecuteParams, ToolResult} from 'common/src/modules/shared/copilot/copilot.types';
import tool from 'common/src/modules/shared/copilot/utils/creators/tool';
import {CLStore} from 'common/src/redux/store';
import _cloneDeep from 'lodash/cloneDeep';
import {z} from 'zod';
import {AdBuilderAgentToolName} from '../adBuilderAgent.enums';

const inputSchema = z.object({
  layerIds: z.array(z.string()).describe('Array of layer IDs to duplicate'),
});

function duplicateLayersTool(store: CLStore) {
  return tool({
    name: AdBuilderAgentToolName.DuplicateLayers,
    displayName: 'Duplicating layer/s',
    description: 'Duplicates specified layers and returns their new IDs',
    inputSchema,
    execute: async ({
      input: {layerIds},
    }: ToolExecuteParams<typeof inputSchema>): Promise<ToolResult> => {
      const activeComp = getActiveComp(store);

      if (!activeComp) {
        return {
          content: 'ERROR: No active composition found',
        };
      }

      try {
        // Get the layers to duplicate
        const layersToDuplicate = activeComp.l.filter(
          (layer) => layerIds.includes(layer.id) && !layer.isLocked,
        );

        if (layersToDuplicate.length === 0) {
          return {
            content:
              'No valid layers found to duplicate. Make sure the layers exist and are not locked.',
          };
        }

        // Deep clone the layers before duplication because:
        // 1. The duplicateLayers function will modify the layers (add mappingId, update name)
        // 2. We don't want to modify the original layers in the composition
        // 3. The duplicateLayers function expects to receive cloned layers that it can modify
        const clonedLayers = layersToDuplicate.map((layer) => _cloneDeep(layer));

        // Duplicate the layers
        const newLayerIds = await store.dispatch(duplicateLayers({layers: clonedLayers})).unwrap();

        if (!newLayerIds || newLayerIds.length === 0) {
          return {
            content: 'Failed to duplicate layers. The operation did not return any new layer IDs.',
          };
        }

        return {
          content: `Duplicated ${layerIds.length} layer(s)`,
        };
      } catch (error) {
        return {
          content: `Failed to duplicate layers: ${error instanceof Error ? error.message : 'Unknown error'}`,
        };
      }
    },
  });
}

export default duplicateLayersTool;
